import React, {useState} from 'react';
import Loader from '../../components/loader/Loader';

import "./ShowMore.scss";

type ShowMoreProps = {
  elements: Array<React.ReactNode>
  // Defines how much elements to render initially wihout hiding them
  minSize?: number
  // Disables the buttons
  disabled?: boolean
  // Renders a loader next to the buttons
  loader?: boolean
  // Enables scroll like function until disabled
  loadMore?: boolean
  // How many elements will be scrolled (only for visualizing)
  loadMoreSize?: number
  // Notify when show more is clicked
  onLoadMore?: () => void
}

const DEFAULT_MIN_SIZE = 5;

/**
 * Simple show more/less component.
 *
 * Work with a collection of React elements.
 */
export function ShowMore(props: ShowMoreProps) {
  const [show, setShow] = useState(false);
  const [hasLoadedMore, setHasLoadedMore] = useState(false);
  const minSize = props.minSize || DEFAULT_MIN_SIZE;

  const toggle = () => setShow(!show);
  const onLoadMore = () => {
    if (props.loadMore && props.onLoadMore) {
      setHasLoadedMore(true);
      props.onLoadMore();
    }
    setShow(true);
  }

  return (
    <>
      <VisibleElements showAll={show} minSize={minSize} elements={props.elements} />
      <div className="ShowMoreButtons">
        <ShowLessButton show={show} onClick={toggle} disabled={props.disabled} />
        { 
          // In case some are loaded and there are more for loading but they were hidden with 
          // show less, fallback to show more to avoid confusion
          props.loadMore && (!hasLoadedMore || show ) ? 
          <LoadMoreButton onClick={onLoadMore} loadNextSize={props.loadMoreSize} disabled={props.disabled}/>
          : 
          <ShowMoreButton show={show} onClick={toggle} minSize={minSize} size={props.elements.length} disabled={props.disabled}/>
        }
        {props.loader ? <Loader size="xs" /> : null}
      </div>
    </>
  );
}

function VisibleElements(props: {
  elements: any
  showAll: boolean
  minSize: number
}) {
  if (props.showAll) {
    return props.elements;
  }
  return props.elements.slice(0, props.minSize);
}

function ShowLessButton(props: {
  show: boolean
  onClick: () => void
  disabled?: boolean
}) {
  if (props.show) {
    return <button className="btn btn-link ShowLessButton"
                   disabled={props.disabled}
                   onClick={props.onClick}>Show less</button>;
  }
  return null;
}

function LoadMoreButton(props: {
  onClick: () => void
  loadNextSize?: number
  disabled?: boolean
}) {
  return <button className="btn btn-link LoadMoreButton"
                 disabled={props.disabled}
                 onClick={props.onClick}>Load {props.loadNextSize} more</button>;
};

function ShowMoreButton(props: {
  onClick: () => void
  show: boolean
  size: number
  minSize: number
  disabled?: boolean
}) {
  // If all are visible & show next is disabled -> no "Show more" button
  if (props.show || props.size <= props.minSize) {
    return null;
  }
  return <button className="btn btn-link ShowMoreButton"
                  disabled={props.disabled}
                  onClick={props.onClick}>Show {props.size - props.minSize} more</button>;
}
